import { produce } from 'immer';
import {
    JOURNEY_UPDATED,
    JOURNEY_RESET,
    JOURNEY_PROGRESS_RESET,
    SESSION_INITIALISED,
    JWT_SET,
    JWT_CLEARED,
    LEAD_ID_SET,
    DOCUMENT_STATUSES_SET,
} from '../types/journey';

const initialState = {
    sessionId: null,
    jwt: null,
    leadId: null,
    currentStage: null,
    currentState: null,
    progressPercent: 0,
    journeyPath: null,
    isCompleted: false,
    dropCode: null,
    esignMismatchError: null,
    postEsignFno: null,
    lastCorrelationId: null,
    documentStatuses: [],
    documentLockMap: {},
    // Rejection Correction session block (spec §8.3). Populated from
    // GET /journey/status envelope `correction: { active, verifiedCount, total }`.
    // Drives resume routing back to the correction page after eSign fail/abandon.
    correction: null,
    // Assisted-journey signal from GET /journey/status (2026-09). `flowType` is the raw
    // leads.flow_type (DIY | ASSISTED | legacy APP/WEB); `isAssisted` is the BE's verdict
    // (flow_type ASSISTED || is_rm_resume_bypass). null = not yet known / no opinion.
    flowType: null,
    isAssisted: null,
    // Alliance signal from GET /journey/status (BE bbd92b62). `isAlliance` true when a
    // partner bank originated the lead; `alliance` is the partner code (e.g. "IDFC"),
    // carried for display/diagnostics only — behaviour keys off isAlliance, never the
    // code. null = not yet known / no opinion.
    isAlliance: null,
    alliance: null,
    // Post-account-creation cross-sell layer from GET /journey/status:
    // { status: PENDING | IN_PROGRESS, position, total } or null (route as today).
    crossSell: null,
};

const journey = produce((draft, action) => {
    switch (action.type) {
        case JOURNEY_UPDATED: {
            const j = action.payload || {};
            // Every API envelope carries a journey snapshot and unconditionally
            // dispatches it (see unwrapEnvelope), with no ordering guarantee
            // between concurrent in-flight requests. A slow call started before
            // a stage-advancing action (e.g. a /journey/status poll in flight
            // when /email/otp/verify resolves) can resolve AFTER it and, without
            // this guard, would silently regress currentStage/currentState back
            // to the earlier value it read — which can then bounce the /dad/*
            // route guard onto an earlier screen the user already passed. Once a
            // later stage is recorded, ignore any snapshot reporting an earlier
            // one outright rather than partially applying its other fields.
            const knownStage = draft.currentStage;
            const incomingStage = j.currentStage;
            const isStaleRegression = incomingStage !== undefined && incomingStage !== null
                && knownStage !== undefined && knownStage !== null
                && Number(incomingStage) < Number(knownStage);
            // Lead-level facts, not progress: apply them even when the snapshot's stage is
            // stale. Only GET /journey/status carries them, and on a fresh signup that call
            // races the OTP-verify envelope (which already reports stage 3 while the lead's
            // stored stage is still 2) — dropping the whole snapshot here silently lost the
            // assisted verdict on first arrival and only a refresh recovered it. null =
            // "no opinion" (same as journeyPath) so other envelopes never wipe them.
            if (j.flowType !== undefined && j.flowType !== null) draft.flowType = j.flowType;
            if (j.isAssisted !== undefined && j.isAssisted !== null) draft.isAssisted = !!j.isAssisted;
            // Same treatment as the assisted verdict: applied before the stale-snapshot
            // guard and only when present, so a later envelope without the key cannot
            // wipe a known alliance verdict.
            if (j.isAlliance !== undefined && j.isAlliance !== null) draft.isAlliance = !!j.isAlliance;
            if (j.alliance !== undefined && j.alliance !== null) draft.alliance = j.alliance;
            if (isStaleRegression) return;
            if (j.currentStage !== undefined) draft.currentStage = j.currentStage;
            if (j.currentState !== undefined) draft.currentState = j.currentState;
            if (j.progressPercent !== undefined) draft.progressPercent = j.progressPercent;
            // journeyPath is a decision made once at the PAN stage (DIGILOCKER_SKIP /
            // DIGILOCKER_REQUIRED) that downstream screens (e.g. dadadhaar's
            // DIGILOCKER_SKIP mount guard, EW-I7260) depend on staying put. Endpoints
            // outside that decision (e.g. GET /aadhaar/details, /aadhaar/digilocker/
            // initiate) don't compute it and send explicit journeyPath:null — treat
            // that as "no opinion", not "clear it", or a later envelope silently wipes
            // out the earlier PAN decision and defeats every journeyPath check for the
            // rest of the session (EW-I7260 KRA-after-non-KRA DigiLocker countdown bug).
            if (j.journeyPath !== undefined && j.journeyPath !== null) draft.journeyPath = j.journeyPath;
            if (j.isCompleted !== undefined) draft.isCompleted = j.isCompleted;
            if (j.dropCode !== undefined) draft.dropCode = j.dropCode;
            if (j.correction !== undefined) draft.correction = j.correction;
            // eSign hard-mismatch reason ("Aadhaar data mismatched with
            // eSign data : <Field> mismatched."), populated by BE only when
            // currentState=DETAILS_DONE after a hard eSign-vs-Aadhaar mismatch.
            if (j.esignMismatchError !== undefined) draft.esignMismatchError = j.esignMismatchError;
            // Post-eSign F&O activation state (OFFER_PENDING / AA_FETCHING /
            // PROOF_RECEIVED / ABANDONED / PROOF_FAILED / DEFERRED / REQ_HELD /
            // REQ_PUSHED / ACTIVATED / RIISE_SIGNIN).
            //
            // Unlike journeyPath above, this is a LIVE lifecycle signal, not a
            // one-shot decision — so its null is not always "no opinion". Which
            // reading applies depends entirely on WHICH envelope carried it:
            //
            //   GET /journey/status  — computes the signal on every call. Its
            //     null is the backend's ANSWER: "no F&O override, route by lead
            //     state" (see PostEsignFnoRouting.ResolveResumePage, which
            //     documents that null as load-bearing and warns callers to
            //     forward rather than swallow it). Apply it verbatim.
            //   everything else — leaves the field null without computing it,
            //     POST /registration/otp/verify included. Ignore that null, or
            //     an unrelated envelope wipes a live offer mid-flow and the F&O
            //     route branch never fires.
            //
            // Swallowing the authoritative null is what stranded an approved
            // non-STP customer: the resolved F&O step stayed AA_FETCHING in the
            // store across an OTP re-auth, so routeForJourney kept routing to
            // the F&O timer page instead of falling through to the
            // ACCOUNT_CREATED -> congratulation mapping (prod lead 23277333).
            if (action.meta && action.meta.fnoAuthoritative) {
                draft.postEsignFno = j.postEsignFno ?? null;
            } else if (j.postEsignFno !== undefined && j.postEsignFno !== null) {
                draft.postEsignFno = j.postEsignFno;
            }
            // Cross-sell layer signal: same rule as postEsignFno, but with its own flag —
            // only GET /journey/status computes it (journeyApi.status marks that call), so
            // only its null means "layer closed"; any other envelope's null is ignored.
            if (action.meta && action.meta.crossSellAuthoritative) {
                draft.crossSell = j.crossSell ?? null;
            } else if (j.crossSell !== undefined && j.crossSell !== null) {
                draft.crossSell = j.crossSell;
            }
            return;
        }
        case SESSION_INITIALISED:
            draft.sessionId = action.payload;
            return;
        case JWT_SET:
            draft.jwt = action.payload;
            return;
        case JWT_CLEARED:
            draft.jwt = null;
            return;
        case LEAD_ID_SET:
            draft.leadId = action.payload;
            return;
        case DOCUMENT_STATUSES_SET: {
            const statuses = action.payload ?? [];
            const lockMap = statuses.reduce((acc, item) => {
                acc[item.documentType] = item.isLocked;
                return acc;
            }, {});
            draft.documentStatuses = statuses;
            draft.documentLockMap = lockMap;
            return;
        }
        case JOURNEY_PROGRESS_RESET:
            // A brand-new application is starting in this tab. Every field below
            // describes the PREVIOUS lead, and none of it can be cleared by the
            // backend: `journeyPath` and `postEsignFno` both treat an incoming
            // null as "no opinion" (see JOURNEY_UPDATED above), and the
            // stale-regression guard makes the new lead's early-stage envelopes
            // look like regressions against the old lead's late stage and drops
            // them wholesale. So the only way a second journey starts clean is an
            // explicit wipe here, at signup.
            //
            // Identity/session (sessionId, jwt, leadId) is deliberately NOT
            // touched: signup is what mints the new jwt/leadId, and sessionId is
            // the Stage-0 arrival session the signup call itself reports.
            //
            // The bug this fixes: a customer who reached the post-eSign F&O offer,
            // had that lead deleted, then signed up again in the same tab was
            // routed straight onto the F&O screen from stage 2, because
            // `postEsignFno: 'OFFER_PENDING'` survived into the new journey
            // (prod lead 23277525 -> 23277930, 2026-08-28).
            draft.currentStage = null;
            draft.currentState = null;
            draft.progressPercent = 0;
            draft.journeyPath = null;
            draft.isCompleted = false;
            draft.dropCode = null;
            draft.esignMismatchError = null;
            draft.postEsignFno = null;
            draft.crossSell = null;
            draft.lastCorrelationId = null;
            draft.documentStatuses = [];
            draft.documentLockMap = {};
            draft.correction = null;
            // Assisted verdict belongs to the previous lead too — a DIY re-signup in a tab
            // that held an assisted journey must not inherit "no camera auto-launch".
            draft.flowType = null;
            draft.isAssisted = null;
            // The alliance verdict belongs to the previous lead too — a DIY re-signup in
            // a tab that held an alliance journey must not inherit the auto-OTP behaviour.
            draft.isAlliance = null;
            draft.alliance = null;
            return;
        case JOURNEY_RESET:
            return initialState;
        default:
            return;
    }
}, initialState);

export default journey;
